import * as Sentry from "@sentry/react";
import { defineMessages } from "react-intl";

import type { GetFlightAvailabilityTokenCabinClass } from "@/types/codegen";

import { CabinClass } from "../types/types";

export const cabinClassOptions = [
  CabinClass.ANY,
  CabinClass.ECONOMY,
  CabinClass.PREMIUM_ECONOMY,
  CabinClass.FIRST_CLASS,
  CabinClass.BUSINESS,
];

export const cabinClassLabels = defineMessages<CabinClass>({
  [CabinClass.ANY]: {
    defaultMessage: "Any",
    description: "Cabin class option: any class (no preference)",
  },
  [CabinClass.ECONOMY]: {
    defaultMessage: "Economy",
    description: "Cabin class option: economy",
  },
  [CabinClass.PREMIUM_ECONOMY]: {
    defaultMessage: "Premium Economy",
    description: "Cabin class option: premium economy",
  },
  [CabinClass.FIRST_CLASS]: {
    defaultMessage: "First Class",
    description: "Cabin class option: first class",
  },
  [CabinClass.BUSINESS]: {
    defaultMessage: "Business",
    description: "Cabin class option: business class",
  },
});

export function isCabinClass(value: string): value is CabinClass {
  return Object.values(CabinClass).includes(value as CabinClass);
}

// For reference:
//
// IRIX Standards:
// Y = Economy,
// W = Premium Economy,
// F = First class,
// C = Business class
//
// Travel Fusion with some differences from IRIX:
// Economy With Restrictions
// Economy Without Restrictions
// Economy Premium
// Business
// First
//
// Amadeus:
// First - class A, F, P, R
// Club (Business) - class C, D, I, J, Z
// W - Economy/Coach Premium
// M - Economy but not Economy/Coach Premium
// Y - Economy (Y = W + M)

function standardizeCabinClassName(cabinClass: string): string {
  switch (cabinClass.trim().toLowerCase()) {
    case "economy":
    case "economy class":
    case "economy standard":
    case "economy with restrictions":
    case "economy without restrictions":
      return CabinClass.ECONOMY;

    case "economy premium":
    case "economy premium class":
    case "premium economy":
    case "premium economy class":
    case "coach premium":
    case "coach premium class":
    case "economy/coach premium":
    case "economy/coach premium class":
      return CabinClass.PREMIUM_ECONOMY;

    case "business":
    case "business class":
    case "(club) business":
    case "(club) business class":
      return CabinClass.BUSINESS;

    case "first":
    case "first class":
      return CabinClass.FIRST_CLASS;

    default:
      return cabinClass;
  }
}

export function determineCabinClasses(
  cabinClasses: GetFlightAvailabilityTokenCabinClass[],
) {
  // unique cabin classes sorted alphabetically
  const results = Array.from(
    new Set(cabinClasses.map((c) => standardizeCabinClassName(c.type))),
  ).sort();

  for (const cabinClass of results) {
    if (!isCabinClass(cabinClass)) {
      Sentry.logger.warn(`Cannot standardize cabin class name`, { cabinClass });
    }
  }

  return results;
}
